/**
 * Cache invalidation for real-time updates
 * Story 253: UI propagation - invalidate React Query caches on WebSocket events
 *
 * Central module for mapping WebSocket events to React Query cache keys
 * and triggering invalidations with debouncing.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { McpStreamEvent } from '../hooks/useUpdatesStream';

/** Debounce delay for rapid invalidations */
const DEBOUNCE_MS = 500;

/**
 * Map an event to the React Query cache keys that should be invalidated.
 *
 * @param event - The WebSocket event received
 * @param projectSlug - Current project identifier
 * @returns Array of query keys to invalidate
 */
export function mapEventToQueryKeys(
  event: McpStreamEvent,
  projectSlug: string
): QueryKey[] {
  const keys: QueryKey[] = [];

  switch (event.entityType) {
    case 'story':
      // Invalidate story list and specific story
      keys.push(['stories', 'list', projectSlug]);
      if (event.storyId) {
        keys.push(['story-detail', projectSlug, event.storyId]);
      }
      break;

    case 'task':
      // Invalidate tasks for the story
      if (event.storyId) {
        keys.push(['story-tasks', projectSlug, event.storyId]);
        // Also invalidate story (task count may have changed)
        keys.push(['story-detail', projectSlug, event.storyId]);
      }
      break;

    case 'document':
    case 'document_section':
      // Invalidate both document types for the story
      // (we don't always know which doc type changed from the event)
      if (event.storyId) {
        keys.push(['story-documents', projectSlug, event.storyId, 'prd']);
        keys.push(['story-documents', projectSlug, event.storyId, 'design']);
      }
      break;

    case 'comment':
      // Comments don't have dedicated cache, but may affect story
      if (event.storyId) {
        keys.push(['story-detail', projectSlug, event.storyId]);
      }
      break;

    case 'epic':
      // Epic changes invalidate epic list and story list (grouping may change)
      keys.push(['epics', projectSlug]);
      keys.push(['stories', 'list', projectSlug]);
      break;

    case 'project':
      // Story 514: a project setting changed; the settings dialog re-reads on next open
      keys.push(['project-settings', projectSlug]);
      break;

    default:
      // Unknown entity type - ignore
      break;
  }

  return keys;
}

/**
 * Invalidate React Query cache for a single event.
 *
 * @param queryClient - React Query client instance
 * @param event - The WebSocket event
 * @param projectSlug - Current project identifier
 */
export function invalidateCacheForEvent(
  queryClient: QueryClient,
  event: McpStreamEvent,
  projectSlug: string
): void {
  const keys = mapEventToQueryKeys(event, projectSlug);

  for (const key of keys) {
    queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * Pending invalidation state for debouncing
 */
interface PendingInvalidation {
  timer: ReturnType<typeof setTimeout>;
  keys: Set<string>;
}

/**
 * Result of creating an invalidation dispatcher
 */
export interface InvalidationDispatcher {
  /** Dispatch function to call on each event */
  dispatch: (event: McpStreamEvent) => void;
  /** Cleanup function to clear pending timers */
  cleanup: () => void;
}

/**
 * Create a debounced invalidation dispatcher.
 *
 * Batches rapid events and invalidates once after the debounce period.
 * This prevents excessive refetches when many events arrive quickly.
 *
 * @param queryClient - React Query client instance
 * @param projectSlug - Current project identifier
 * @param debounceMs - Debounce delay in milliseconds (default: 500)
 * @returns Object with dispatch and cleanup functions
 */
export function createInvalidationDispatcher(
  queryClient: QueryClient,
  projectSlug: string,
  debounceMs: number = DEBOUNCE_MS
): InvalidationDispatcher {
  const pending: PendingInvalidation = {
    timer: undefined as unknown as ReturnType<typeof setTimeout>,
    keys: new Set<string>(),
  };

  const flush = () => {
    // Invalidate all accumulated keys
    for (const keyStr of pending.keys) {
      const key = JSON.parse(keyStr) as QueryKey;
      queryClient.invalidateQueries({ queryKey: key });
    }
    pending.keys.clear();
  };

  const dispatch = (event: McpStreamEvent) => {
    // Map event to keys and accumulate
    const keys = mapEventToQueryKeys(event, projectSlug);
    for (const key of keys) {
      pending.keys.add(JSON.stringify(key));
    }

    // Reset debounce timer
    clearTimeout(pending.timer);
    pending.timer = setTimeout(flush, debounceMs);
  };

  const cleanup = () => {
    clearTimeout(pending.timer);
    pending.keys.clear();
  };

  return { dispatch, cleanup };
}

/**
 * Handle WebSocket reconnection by invalidating all project caches.
 *
 * When reconnecting, we may have missed events, so safest to refetch everything.
 *
 * @param queryClient - React Query client instance
 * @param projectSlug - Current project identifier
 */
export function handleReconnection(
  queryClient: QueryClient,
  projectSlug: string
): void {
  // Invalidate all project-scoped queries
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!Array.isArray(key) || key.length < 2) return false;

      // Check if this query is for the current project
      // Most keys have projectSlug at index 2 (e.g., ['stories', 'list', projectSlug])
      // Some have it at different positions
      return (
        key.includes(projectSlug) &&
        (key[0] === 'stories' ||
          key[0] === 'story-detail' ||
          key[0] === 'story-documents' ||
          key[0] === 'story-tasks' ||
          key[0] === 'epics')
      );
    },
  });
}
